import { decodeAudioDataCompat } from "./browserCompatibility";
import { SITE_TUNING } from "./siteTuning";

type PictionarySound = "rightAnswer" | "wordStart" | "wordFinished" | "countdownTick" | "countdownTock";

const SOUND_PATHS: Record<PictionarySound, string> = {
    rightAnswer: "/audio/pictionary/right-answer.mp3",
    wordStart: "/audio/pictionary/word-start.mp3",
    wordFinished: "/audio/pictionary/word-finished.mp3",
    countdownTick: "/audio/pictionary/countdown-tick.mp3",
    countdownTock: "/audio/pictionary/countdown-tock.mp3"
};

const SOUND_VOLUME: Record<PictionarySound, number> = {
    rightAnswer: SITE_TUNING.pictionary.audio.rightAnswerVolume,
    wordStart: SITE_TUNING.pictionary.audio.wordStartVolume,
    wordFinished: SITE_TUNING.pictionary.audio.wordFinishedVolume,
    countdownTick: SITE_TUNING.pictionary.audio.countdownTickVolume,
    countdownTock: SITE_TUNING.pictionary.audio.countdownTockVolume
};

const GLOBAL_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.pictionary.audio.globalVolumeMultiplier;
const MOBILE_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.pictionary.audio.mobileVolumeMultiplier;
const MOBILE_SOUND_SHORT_SIDE_MAX_PX = SITE_TUNING.pictionary.audio.mobileVolumeShortSideMaxPx;
const THINKING_TRACK_PATHS = [
    "/audio/pictionary/thinking-1.mp3",
    "/audio/pictionary/thinking-2.mp3"
] as const;

export class PictionaryAudio {
    private context: AudioContext | null = null;
    private readonly buffers = new Map<PictionarySound, AudioBuffer>();
    private readonly loading = new Map<PictionarySound, Promise<void>>();
    private pendingSounds: { sound: PictionarySound; requestedAt: number }[] = [];
    private disposed = false;
    private primed = false;
    private contextToken = 0;
    private thinkingTrack: HTMLAudioElement | null = null;
    private thinkingTrackIndex: number | null = null;
    private readonly thinkingPositions = new Map<number, number>();
    private thinkingPlaybackBlocked = false;
    private thinkingShouldPlay = false;

    public constructor() {
        document.addEventListener("pointerdown", this.unlockThinkingMusic);
        document.addEventListener("keydown", this.unlockThinkingMusic);
    }

    public async prepare(): Promise<void> {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        await Promise.all(Object.keys(SOUND_PATHS).map((sound) => this.load(sound as PictionarySound)));
        this.flushPendingSounds();
    }

    public playRightAnswer() {
        this.play("rightAnswer");
    }

    public playWordStart() {
        this.play("wordStart");
    }

    public playWordFinished() {
        this.play("wordFinished");
    }

    public playCountdownTick() {
        this.play("countdownTick");
    }

    public playCountdownTock() {
        this.play("countdownTock");
    }

    public syncThinkingMusic(isHost: boolean, isThinking: boolean, roundNumber: number) {
        this.thinkingShouldPlay = !this.disposed && isHost && isThinking;
        if (!this.thinkingShouldPlay) {
            this.pauseThinkingMusic();
            return;
        }

        const nextIndex = Math.abs(Math.max(1, roundNumber) - 1) % THINKING_TRACK_PATHS.length;

        if (this.thinkingTrackIndex !== nextIndex || !this.thinkingTrack) {
            this.pauseThinkingMusic(true);
            const track = new Audio(THINKING_TRACK_PATHS[nextIndex]);
            track.preload = "auto";
            track.loop = true;
            track.volume = SITE_TUNING.pictionary.audio.thinkingMusicVolume;
            const resumeAt = this.thinkingPositions.get(nextIndex) ?? 0;
            track.addEventListener("loadedmetadata", () => {
                track.currentTime = resumeAt < track.duration ? resumeAt : 0;
            }, { once: true });
            this.thinkingTrack = track;
            this.thinkingTrackIndex = nextIndex;
            this.thinkingPlaybackBlocked = false;
            track.load();
        }

        this.playThinkingMusic();
    }

    public dispose() {
        this.disposed = true;
        this.pendingSounds = [];
        this.pauseThinkingMusic(true);
        document.removeEventListener("pointerdown", this.unlockThinkingMusic);
        document.removeEventListener("keydown", this.unlockThinkingMusic);
        const context = this.context;
        this.resetContext(context, { clearBuffers: true });
        context?.close().catch(() => undefined);
    }

    private readonly unlockThinkingMusic = () => {
        // Music and effects have separate browser autoplay permissions.
        void this.prepare();
        this.thinkingPlaybackBlocked = false;
        if (this.thinkingShouldPlay) {
            this.playThinkingMusic();
        }
    };

    private playThinkingMusic() {
        const track = this.thinkingTrack;

        if (!track || !track.paused || this.thinkingPlaybackBlocked) {
            return;
        }

        void track.play().catch((error: { name?: string } | undefined) => {
            if (error?.name === "AbortError") {
                return;
            }

            this.thinkingPlaybackBlocked = true;
            console.warn(`[picture-detectives:audio] thinking_music_blocked track=${(this.thinkingTrackIndex ?? 0) + 1} error=${error?.name ?? "unknown"}`);
        });
    }

    private pauseThinkingMusic(release = false) {
        if (!this.thinkingTrack) {
            return;
        }

        if (this.thinkingTrackIndex !== null && Number.isFinite(this.thinkingTrack.currentTime)) {
            this.thinkingPositions.set(this.thinkingTrackIndex, this.thinkingTrack.currentTime);
        }

        this.thinkingTrack.pause();

        if (release) {
            this.thinkingTrack.removeAttribute("src");
            this.thinkingTrack.load();
            this.thinkingTrack = null;
            this.thinkingTrackIndex = null;
        }
    }

    private getContext(): AudioContext | null {
        if (this.disposed) {
            return null;
        }

        if (this.context?.state === "closed") {
            this.resetContext(this.context);
        }

        if (this.context) {
            return this.context;
        }

        const AudioContextConstructor =
            window.AudioContext ??
            (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;

        if (!AudioContextConstructor) {
            return null;
        }

        const context = new AudioContextConstructor();
        this.context = context;
        this.contextToken += 1;
        context.addEventListener("statechange", this.flushPendingSounds);
        return context;
    }

    private async ensureContextReady(): Promise<AudioContext | null> {
        const context = this.getContext();

        if (!context) {
            return null;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state === "running") {
            this.primeContext(context);
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return null;
        }

        return context;
    }

    private primeContext(context: AudioContext) {
        if (this.primed) {
            return;
        }

        try {
            const buffer = context.createBuffer(1, 1, Math.max(22050, context.sampleRate || 22050));
            const source = context.createBufferSource();
            const gain = context.createGain();

            gain.gain.value = 0;
            source.buffer = buffer;
            source.connect(gain);
            gain.connect(context.destination);
            source.start(0);
            source.onended = () => {
                source.disconnect();
                gain.disconnect();
            };
            this.primed = true;
        } catch {
            this.primed = true;
        }
    }

    private load(sound: PictionarySound): Promise<void> {
        if (this.buffers.has(sound)) {
            return Promise.resolve();
        }

        const existingLoad = this.loading.get(sound);

        if (existingLoad) {
            return existingLoad;
        }

        const context = this.getContext();

        if (!context) {
            return Promise.resolve();
        }

        const contextToken = this.contextToken;
        let loadPromise: Promise<void>;
        loadPromise = fetch(SOUND_PATHS[sound])
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Unable to load ${sound} sound`);
                }

                return response.arrayBuffer();
            })
            .then((arrayBuffer) => {
                if (this.isContextStale(context, contextToken)) {
                    return null;
                }

                return decodeAudioDataCompat(context, arrayBuffer);
            })
            .then((buffer) => {
                if (!buffer || this.isContextStale(context, contextToken)) {
                    return;
                }

                this.buffers.set(sound, buffer);
            })
            .catch((error) => this.warn(`load_failed sound=${sound} error=${error?.name ?? "unknown"}`))
            .finally(() => {
                if (this.loading.get(sound) === loadPromise) {
                    this.loading.delete(sound);
                }
            });

        this.loading.set(sound, loadPromise);
        return loadPromise;
    }

    private play(sound: PictionarySound) {
        if (this.disposed) return;
        this.pendingSounds.push({ sound, requestedAt: Date.now() });
        // Loading must not wait for resume(): browsers can leave it pending
        // until the next gesture, even while HTML music is already playing.
        void this.load(sound).then(this.flushPendingSounds);
        void this.ensureContextReady().then(this.flushPendingSounds).catch((error) => {
            this.warn(`resume_failed sound=${sound} error=${error?.name ?? "unknown"}`);
        });
    }

    private readonly flushPendingSounds = () => {
        const context = this.context;
        if (this.disposed) return;
        this.pendingSounds = this.pendingSounds.filter(({ sound, requestedAt }) => {
            // Never replay old feedback (especially countdown ticks) after a
            // long background interruption or an eventual autoplay unlock.
            const maxAge = sound === "countdownTick" || sound === "countdownTock" ? 300 : 2000;
            if (Date.now() - requestedAt > maxAge) {
                this.warn(`cue_expired sound=${sound} state=${context?.state ?? "unavailable"} buffered=${this.buffers.has(sound)}`);
                return false;
            }
            const buffer = this.buffers.get(sound);
            if (!context || context.state !== "running" || !buffer) return true;
            this.startBufferedSound(context, sound, buffer);
            return false;
        });
    };

    private warn(message: string) {
        try {
            if (localStorage.getItem("ziarcadia:pictionary-audio-debug") === "0") return;
        } catch {
            // Storage can be disabled independently of audio.
        }
        console.warn(`[picture-detectives:audio] ${message}`);
    }

    private startBufferedSound(context: AudioContext, sound: PictionarySound, buffer: AudioBuffer) {
        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        gain.gain.value = this.getOutputVolume(sound);
        source.connect(gain);
        gain.connect(context.destination);
        source.onended = () => {
            source.disconnect();
            gain.disconnect();
        };
        source.start();
    }

    private resetContext(context: AudioContext | null, options: { clearBuffers?: boolean } = {}) {
        if (context && this.context && context !== this.context) {
            return;
        }

        context?.removeEventListener("statechange", this.flushPendingSounds);
        this.context = null;
        this.primed = false;
        this.loading.clear();

        if (options.clearBuffers) {
            this.buffers.clear();
        }

        this.contextToken += 1;
    }

    private isContextStale(context: AudioContext, contextToken: number): boolean {
        if (this.disposed || this.context !== context || this.contextToken !== contextToken) {
            return true;
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return true;
        }

        return false;
    }

    private getOutputVolume(sound: PictionarySound): number {
        const baseVolume = SOUND_VOLUME[sound] * GLOBAL_SOUND_VOLUME_MULTIPLIER;

        if (!this.isPhoneSizedTouchDevice()) {
            return baseVolume;
        }

        return baseVolume * MOBILE_SOUND_VOLUME_MULTIPLIER;
    }

    private isPhoneSizedTouchDevice(): boolean {
        const shortSide = Math.min(window.innerWidth || Number.POSITIVE_INFINITY, window.innerHeight || Number.POSITIVE_INFINITY);
        const hasTouchPrimaryInput =
            navigator.maxTouchPoints > 0 ||
            window.matchMedia("(pointer: coarse)").matches ||
            window.matchMedia("(any-pointer: coarse)").matches;

        return hasTouchPrimaryInput && shortSide <= MOBILE_SOUND_SHORT_SIDE_MAX_PX;
    }
}
